import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { SEO } from "@/components/common/SEO";
import { PageHero } from "@/components/common/PageHero";
import { ContactCta } from "@/components/common/DetailPage";
import { imageFor } from "@/lib/imagery";
import { COMPANY } from "@/data/company";
import type { RouteKey } from "@/lib/routes";

const RELATED: RouteKey[] = ["compliance.kyc", "compliance.antifraud"];

interface Row {
  label: string;
  value: string;
  href?: string;
}
interface Group {
  heading: string;
  rows: Row[];
}

function groupsFor(locale: "en" | "it"): Group[] {
  const en = locale === "en";
  return [
    {
      heading: en ? "Company registration" : "Iscrizione societaria",
      rows: [
        { label: en ? "Legal name" : "Denominazione", value: COMPANY.legalName },
        { label: en ? "Legal form" : "Forma giuridica", value: COMPANY.legalForm },
        { label: en ? "Country" : "Paese", value: COMPANY.address.country[locale] },
        { label: en ? "Status" : "Stato", value: COMPANY.status[locale] },
        { label: "P. IVA", value: COMPANY.vat },
        { label: en ? "Tax Code (Codice Fiscale)" : "Codice Fiscale", value: COMPANY.taxCode },
        { label: "REA", value: COMPANY.rea },
        { label: en ? "Registered with" : "Iscritta presso", value: COMPANY.registeredWith },
        { label: en ? "Share capital" : "Capitale sociale", value: COMPANY.shareCapital[locale] },
      ],
    },
    {
      heading: en ? "Registered office" : "Sede legale",
      rows: [
        {
          label: en ? "Address" : "Indirizzo",
          value: `${COMPANY.address.street}, ${COMPANY.address.city}, ${COMPANY.address.country[locale]}`,
          href: COMPANY.mapsUrl,
        },
      ],
    },
    {
      heading: en ? "Management" : "Organi sociali",
      rows: [
        { label: en ? "Chairman" : "Presidente", value: COMPANY.chairman },
        { label: en ? "Board Member" : "Consigliere", value: COMPANY.boardMember },
      ],
    },
    {
      heading: en ? "Customs identifiers" : "Identificativi doganali",
      rows: [
        { label: en ? "European Union EORI" : "EORI Unione Europea", value: COMPANY.eoriEU },
        { label: en ? "United Kingdom EORI" : "EORI Regno Unito", value: COMPANY.eoriUK },
      ],
    },
    {
      heading: en ? "Official channels" : "Canali ufficiali",
      rows: [
        { label: en ? "Website" : "Sito web", value: COMPANY.websiteLabel, href: COMPANY.website },
        { label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
        { label: "PEC", value: COMPANY.pec, href: `mailto:${COMPANY.pec}` },
        { label: en ? "Telephone" : "Telefono", value: COMPANY.phone, href: COMPANY.phoneHref },
      ],
    },
  ];
}

export default function CorporateInformation() {
  const { t, path, locale } = useT();
  const img = imageFor("compliance.corporate");

  return (
    <>
      <SEO routeKey="compliance.corporate" />
      <PageHero routeKey="compliance.corporate" imageUrl={img?.url} imageAlt={img?.alt} />

      <section className="container-bnm py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <aside className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
            <p className="eyebrow mb-4">{t.sections.scopeLabel}</p>
            <p className="text-slate2 max-w-prose-tight leading-relaxed">
              {(t.routes["compliance.corporate"] as { lede?: string }).lede}
            </p>
          </aside>

          <div className="lg:col-span-8 flex flex-col gap-12">
            {groupsFor(locale).map((group) => (
              <div key={group.heading} data-testid={`corporate-group-${group.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <p className="eyebrow mb-5">{group.heading}</p>
                <dl className="flex flex-col">
                  {group.rows.map((row) => (
                    <div
                      key={row.label}
                      className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6 border-b border-bone-500 py-3"
                    >
                      <dt className="font-mono text-[11px] uppercase tracking-eyebrow text-slate2 shrink-0">
                        {row.label}
                      </dt>
                      <dd className="text-ink text-sm sm:text-right">
                        {row.href ? (
                          <a
                            href={row.href}
                            className="hover:text-clay transition-colors border-b border-transparent hover:border-clay"
                            {...(row.href.startsWith("http")
                              ? { target: "_blank", rel: "noopener noreferrer" }
                              : {})}
                          >
                            {row.value}
                          </a>
                        ) : (
                          row.value
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-bone-500 bg-bone-300/40">
        <div className="container-bnm py-16 md:py-20">
          <p className="eyebrow mb-8">{t.sections.seeAlso}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {RELATED.map((k) => (
              <Link
                key={k}
                to={path(k)}
                className="group border border-bone-500 bg-bone p-6 md:p-8 hover:border-ink transition-colors"
                data-testid={`related-${k.replace(".", "-")}`}
              >
                <p className="eyebrow mb-4">{(t.routes[k] as { eyebrow?: string }).eyebrow}</p>
                <h3 className="font-display text-xl md:text-2xl text-ink group-hover:text-clay transition-colors">
                  {t.routes[k].label}
                </h3>
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-eyebrow text-slate2 group-hover:text-clay transition-colors">
                  <ArrowRight size={14} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCta />
    </>
  );
}
